import { Link } from "react-router-dom";
import { X, MessageCircle } from "lucide-react";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { Mark } from "@/components/Logo";
import { QtyStepper } from "@/components/ProductDrawer";
import { useLang } from "@/context/LangContext";
import { useCart } from "@/context/CartContext";
import { useStore } from "@/context/StoreContext";
import { imgUrl, waLink } from "@/lib/api";

function CartWa({ testid }) {
  const { t } = useLang();
  const { settings } = useStore();
  if (!settings?.whatsapp_number) return null;
  return (
    <a data-testid={testid} href={waLink(settings.whatsapp_number, t("wa_contact_msg"))} target="_blank" rel="noreferrer" className="mt-3 flex items-center justify-center gap-2 border border-[#241914]/20 py-3.5 text-[0.72rem] tracking-[0.2em] text-[#241914] transition-colors hover:border-[#C6A76A] hover:text-[#8a6d35] rtl:text-sm rtl:tracking-normal">
      <MessageCircle className="h-4 w-4" strokeWidth={1.3} /> {t("contact_wa")}
    </a>
  );
}

export function CartDrawer() {
  const { t, tr, fmt, rtl } = useLang();
  const { items, open, setOpen, setQty, remove, subtotal } = useCart();
  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent side={rtl ? "left" : "right"} data-lenis-prevent data-testid="cart-drawer" className="flex w-full flex-col border-[#C6A76A]/20 bg-[#F7F3EB] p-0 text-[#0B0B0C] sm:max-w-md">
        <div className="flex items-center justify-between border-b border-[#241914]/10 px-7 py-6">
          <SheetTitle className="font-display text-2xl font-normal">{t("bag")}</SheetTitle>
        </div>
        {items.length === 0 ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-6 px-8 text-center" data-testid="cart-empty">
            <Mark className="h-16 w-14 text-[#C6A76A]" />
            <p className="font-display text-2xl">{t("bag_empty")}</p>
            <button data-testid="cart-continue-btn" onClick={() => setOpen(false)} className="link-line pb-1 text-xs tracking-[0.22em] rtl:text-sm rtl:tracking-normal">
              {t("continue_shopping")}
            </button>
            <CartWa testid="cart-empty-whatsapp-btn" />
          </div>
        ) : (
          <>
            <div className="flex-1 divide-y divide-[#241914]/10 overflow-y-auto px-7">
              {items.map((i) => (
                <div key={i.product_id} className="flex gap-4 py-6" data-testid={`cart-item-${i.product_id}`}>
                  <div className="arch-sm h-28 w-20 shrink-0 overflow-hidden bg-[#E8DFD0]">
                    {i.image ? <img src={imgUrl(i.image)} alt="" className="h-full w-full object-cover" /> : <Mark className="m-auto mt-8 h-10 w-8 text-[#C6A76A]" />}
                  </div>
                  <div className="flex flex-1 flex-col">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className="font-display text-xl leading-tight">{tr(i.name)}</p>
                        {i.volume && <p className="mt-1 font-latin text-xs tracking-[0.2em] text-[#241914]/60">{i.volume}</p>}
                      </div>
                      <button data-testid={`cart-remove-${i.product_id}`} onClick={() => remove(i.product_id)} aria-label={t("remove")} className="p-1 text-[#241914]/50 hover:text-[#0B0B0C]">
                        <X className="h-4 w-4" strokeWidth={1.3} />
                      </button>
                    </div>
                    <div className="mt-auto flex items-center justify-between pt-3">
                      <QtyStepper value={i.qty} onChange={(q) => setQty(i.product_id, q)} max={i.stock} testid={`cart-qty-${i.product_id}`} />
                      <span className="font-latin text-sm rtl:font-sans">{fmt(i.price * i.qty)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="border-t border-[#241914]/10 bg-[#E8DFD0]/50 px-7 py-6">
              <div className="flex justify-between text-sm">
                <span>{t("subtotal")}</span>
                <span data-testid="cart-subtotal" className="font-latin rtl:font-sans">{fmt(subtotal)}</span>
              </div>
              <p className="mt-2 text-xs text-[#241914]/65">{t("delivery_at_checkout")}</p>
              <Link
                to="/checkout"
                data-testid="cart-checkout-btn"
                onClick={() => setOpen(false)}
                className="mt-6 block bg-[#0B0B0C] py-4 text-center text-[0.78rem] tracking-[0.25em] text-[#F7F3EB] transition-colors duration-500 hover:bg-[#241914] rtl:text-base rtl:tracking-normal"
              >
                {t("checkout")}
              </Link>
              <CartWa testid="cart-whatsapp-btn" />
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
